/**
 * The stats page's boot values — what is stamped into the document once and does not change
 * again. One home for them, so a surface that needs the account id or the job endpoint imports it
 * instead of reaching into the global itself.
 *
 * Nearly all of them come from the server. The window uid is the exception and is minted here,
 * because it has to differ between two windows showing the same report and the document they are
 * both rendered from can be served from the browser cache — a server-minted value would be
 * identical in both, and the drag-and-drop guard that compares it would stop rejecting a drag
 * that crossed windows. A fresh script realm per window makes the mint unique by construction.
 *
 * The line between this file and `stat-page.js` is what the page does with the value after boot.
 * Anything it rewrites — the active report, the survey, the saved palettes and presets, the
 * report-asset fields — is live state and belongs there, read at call time. Anything stamped once
 * belongs here. A value that starts being rewritten moves out of this file rather than growing a
 * special case inside it.
 *
 * Read through the property rather than captured into a module constant, and that is deliberate.
 * A constant is evaluated when the module first loads, which is correct on the real page — the
 * layout emits these in an inline script above every script tag — but wrong everywhere the page is
 * fabricated rather than served. The suite does that in forty-four places: a test assigns
 * `window.features`, asserts, assigns a different value and asserts again, and a captured constant
 * cannot follow it. The property read costs nothing worth measuring and keeps every one of those
 * honest.
 */

import { generateUID } from '@aytm/app/stats/lib/util/helpers'

const w = () => ('undefined' === typeof window ? {} : window)

if ('undefined' !== typeof window) window.windowUID ??= generateUID()

const PageGlobals = {
  // --- identity ---
  get SURVEY_ID() {
    return w().surveyId
  },
  get USER_ID() {
    return w().userId
  },
  get ACCOUNT_ID() {
    return w().accountId
  },
  get ACCOUNT_NAME() {
    return w().accountName || 'Personal'
  },
  get TEAM_ID() {
    return w().teamId
  },
  get USERNAME() {
    return w().username
  },
  get USER_ROLES() {
    return w().userRoles
  },
  get SHARE_KEY() {
    return w().shareKey
  },

  // Namespaces this window's drag-and-drop types, so a drag begun in another window carrying the
  // same report is refused rather than dropped into this one.
  get WINDOW_UID() {
    return w().windowUID
  },

  // --- service endpoints ---
  get STAT_SERVER_URL() {
    return w().statServerUrl
  },
  get STAT_REPORT_URL() {
    return w().statReportUrl
  },
  get STAT_JOB_API_URL() {
    return w().statJobApiUrl
  },
  get AYTM_DOMAIN() {
    return w().aytm_domain
  },
  get SUBDOMAIN() {
    return w().subdomain
  },

  // --- per-survey paths the page was rendered with ---
  get SURVEY_PREVIEW_PATH() {
    return w().survey_preview_path
  },
  get PREVIEW_TOKEN_PATH() {
    return w().preview_token_path
  },
  get DRAFT_SURVEY_PATH() {
    return w().draft_survey_path
  },
  get CREATE_WORD_CLOUD_PATH() {
    return w().createWordCloudPath
  },
  get CREATE_EXPERT_STUDY_REPORT_PATH() {
    return w().createExpertStudyReportPath
  },

  // --- capability and entitlement ---
  get CAN_CREATE_ATTACHMENT() {
    return w().can_create_attachment
  },
  get CAN_VIEW_ATTACHMENT() {
    return w().can_view_attachment
  },
  get ANALYSIS_UNLOCK_N() {
    return w().analysis_unlock_n
  },
  get ALLOW_QUESTION_TYPES() {
    return w().allow_question_types
  },

  // --- page-load catalogues ---
  get CONFIDENCE_LEVELS() {
    return w().confidence_levels
  },
  get CUSTOM_TEMPLATES() {
    return w().customTemplates
  },

  // Question/tool code -> icon-font glyph name, keyed by the same codes the report addresses
  // questions by.
  get QUESTION_TYPE_ICONS() {
    return w().icon_question_types || {}
  },
  get SMILE_ANSWERS() {
    return w().smileAnswers || []
  },

  // --- survey facts the page was rendered with ---
  get SURVEY_STARTED_AT() {
    return w().survey_started_at
  },
  get SURVEY_FINISHED_AT() {
    return w().survey_finished_at
  },
  get IS_EXTERNAL_SURVEY() {
    return w().isExternalSurvey
  },
  get HAS_FAKE_RESPONSES() {
    return w().has_fake_responses
  },
  get TIME_ZONE() {
    return w().timeZone
  },
  get SURVEY_TIME_ZONE() {
    return w().surveyTimeZone
  },
  get SERVER_TIME() {
    return w().server_time
  }
}

export default PageGlobals
